import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    Check,
    Inbox,
    ShieldCheck,
    Archive,
    Loader2,
    RotateCcw,
    Eye,
    History,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import ProjectHeader from "../components/ProjectHeader";
import SegmentedControl from "../components/SegmentedControl";
import { useAuth } from "../context/AuthContext";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { formatRelativeTime } from "../lib/media";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

// Iter-114b — two tabs only. Acknowledge is per-user (metadata on the
// card) — it does NOT change global lifecycle state. Only Resolve and
// Reopen flip the global state.
// Iter-116 PART 3 — three tabs. Inbox = active flags directed at me.
// Sent = active flags I sent (tracking outcome). Archive = resolved
// flags I'm involved in.
const TABS = [
    { id: "inbox", label: "Inbox" },
    { id: "sent", label: "Sent" },
    { id: "archived", label: "Archive" },
];

function flagEntityHref(routeBase, projectId, flag) {
    const t = flag?.entity_type || "state_entry";
    const eid = flag?.entity_id || flag?.entry_id;
    const fid = flag?.id;
    if (!eid) return null;
    let base;
    switch (t) {
        case "state_entry":
        case "image_note":
            base = `${routeBase}/${projectId}/state-entries/${eid}`;
            break;
        case "scene":
        case "scene_note":
            base = `${routeBase}/${projectId}/scenes/${eid}`;
            break;
        case "character":
        case "character_note":
            base = `${routeBase}/${projectId}/characters/${eid}`;
            break;
        case "supervisor_note":
        case "script_note":
            base = `${routeBase}/${projectId}/supervisor-notes`;
            break;
        case "continuity_note":
        case "document":
            base = `${routeBase}/${projectId}/supervisor-notes`;
            break;
        case "schedule":
        case "schedule_note":
            base = `${routeBase}/${projectId}/schedule`;
            break;
        default:
            return null;
    }
    // Iter-116 PART 1 — append ?flag={id} so the global FlagPanel in
    // ProjectHeader auto-opens on arrival.
    return fid ? `${base}?flag=${fid}` : base;
}

function entityLabel(t) {
    switch (t) {
        case "state_entry": return "Image";
        case "image_note": return "Image note";
        case "scene": return "Scene";
        case "scene_note": return "Scene note";
        case "character": return "Cast";
        case "character_note": return "Cast note";
        case "supervisor_note": return "Note";
        case "script_note": return "Script note";
        case "continuity_note": return "Continuity";
        case "document": return "Document";
        case "schedule": return "Schedule";
        case "schedule_note": return "Schedule note";
        default: return t || "Item";
    }
}

function FlagCard({
    flag,
    projectId,
    routeBase,
    user,
    canManage,
    canAct = true,
    isSentView,
    onAcknowledge,
    onOpenResolve,
    onOpenReopen,
}) {
    const isResolved = flag.status === "resolved";
    const isReopened = flag.status === "reopened";
    const isActive = flag.status === "pending" || isReopened;
    const recipientIds = (flag.recipients || []).map((r) => r.user_id);
    const iAmRecipient =
        recipientIds.includes(user?.id) || flag.flagged_for === user?.id;
    const iAmSender = flag.flagged_by === user?.id;
    const acknowledgedBy = Array.isArray(flag.acknowledged_by)
        ? flag.acknowledged_by
        : [];
    const iHaveAcked = acknowledgedBy.includes(user?.id);
    const href = flagEntityHref(routeBase, projectId, flag);

    // Visual treatment:
    //  * action required (active + I haven't acked + I'm a recipient) → strong amber
    //  * active but I've personally acked (or not a recipient)         → faded amber
    //  * resolved                                                     → neutral
    const actionRequired = isActive && iAmRecipient && !iHaveAcked;
    const cardClass = isResolved
        ? "border-white/10 bg-card"
        : actionRequired
            ? "border-amber-500/40 bg-amber-500/8"
            : "border-amber-500/20 bg-amber-500/4";
    const iconBgClass = isResolved
        ? "bg-neutral-500/15 text-neutral-400"
        : actionRequired
            ? "bg-amber-500/20 text-amber-300"
            : "bg-amber-500/10 text-amber-200/80";

    const acknowledgements = flag.acknowledgements || [];

    return (
        <div
            data-testid={`flag-card-${flag.id}`}
            data-status={flag.status}
            data-action-required={actionRequired ? "true" : "false"}
            className={`rounded-xl border p-4 ${cardClass}`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${iconBgClass}`}
                >
                    {isResolved ? (
                        <Archive size={16} />
                    ) : isReopened ? (
                        <RotateCcw size={16} />
                    ) : (
                        <AlertTriangle size={16} />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            {entityLabel(flag.entity_type)}
                        </span>
                        {isReopened ? (
                            <span
                                data-testid={`flag-status-reopened-${flag.id}`}
                                className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold"
                            >
                                Reopened
                            </span>
                        ) : null}
                        {/* Iter-116 PART 2 — "Action required" badge
                            removed. The header bell + amber card border
                            are the single attention indicator. */}
                        {isActive && iAmRecipient && iHaveAcked ? (
                            <span
                                data-testid={`flag-seen-by-you-${flag.id}`}
                                className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80 inline-flex items-center gap-1"
                            >
                                <Eye size={10} /> Seen by you
                            </span>
                        ) : null}
                    </div>
                    <p className="text-sm text-white mt-1 break-words">
                        {flag.note || (
                            <span className="italic text-neutral-400">
                                (no message)
                            </span>
                        )}
                    </p>
                    <div className="text-[10px] text-neutral-500 mt-1">
                        From {flag.flagged_by_name || "—"} ·{" "}
                        {formatRelativeTime(flag.created_at)}
                        {isResolved && flag.resolved_by_name ? (
                            <>
                                {" · resolved by "}
                                {flag.resolved_by_name}
                                {flag.resolved_at ? (
                                    <>
                                        {" "}
                                        · {formatRelativeTime(flag.resolved_at)}
                                    </>
                                ) : null}
                            </>
                        ) : null}
                        {isReopened && flag.reopened_by_name ? (
                            <>
                                {" · reopened by "}
                                {flag.reopened_by_name}
                                {flag.reopened_at ? (
                                    <>
                                        {" "}
                                        · {formatRelativeTime(flag.reopened_at)}
                                    </>
                                ) : null}
                            </>
                        ) : null}
                    </div>

                    {/* Personal-ack roster — who has personally acknowledged
                        this flag. Visible on active flags only; on archive
                        cards the resolution becomes the headline.
                        Iter-116 PART 3 — on the Sent tab, ALWAYS render
                        an ack progress chip even when nobody has acked
                        yet (e.g. "0 of 3 seen"), so the sender always
                        sees outcome state at a glance. */}
                    {isActive && (acknowledgements.length > 0 || isSentView) ? (
                        <div
                            data-testid={`flag-ack-roster-${flag.id}`}
                            className="mt-2 flex flex-wrap items-center gap-1.5"
                        >
                            <span
                                data-testid={`flag-ack-progress-${flag.id}`}
                                className="text-[9px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                {isSentView && (flag.recipients || []).length > 0
                                    ? `${acknowledgements.length} of ${flag.recipients.length} seen`
                                    : "Acknowledged by"}
                            </span>
                            {acknowledgements.length === 0 && isSentView ? (
                                <span className="text-[10px] text-amber-300/80">
                                    awaiting acknowledgement
                                </span>
                            ) : null}
                            {acknowledgements.map((a) => (
                                <span
                                    key={a.user_id}
                                    className="inline-flex items-center gap-1 text-[10px] text-emerald-300/90 bg-emerald-500/8 border border-emerald-500/25 rounded-full px-1.5 py-0.5"
                                >
                                    <Check size={9} />
                                    {a.user_id === user?.id
                                        ? "You"
                                        : a.name || "—"}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    {/* Resolution body — appears on resolved flags. */}
                    {isResolved && flag.resolution_note ? (
                        <div className="mt-2 text-xs text-neutral-300 px-2.5 py-1.5 rounded-md bg-emerald-500/8 border border-emerald-500/20">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">
                                Resolution
                            </span>
                            <p
                                data-testid={`flag-resolution-note-${flag.id}`}
                                className="mt-0.5"
                            >
                                {flag.resolution_note}
                            </p>
                        </div>
                    ) : null}

                    {/* Current reopen note — for reopened flags. */}
                    {isReopened && flag.reopen_note ? (
                        <div className="mt-2 text-xs text-neutral-200 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300/90">
                                Reopen note
                            </span>
                            <p
                                data-testid={`flag-reopen-note-${flag.id}`}
                                className="mt-0.5"
                            >
                                {flag.reopen_note}
                            </p>
                        </div>
                    ) : null}

                    {/* Reopen history — every cycle's resolution + reopen
                        note remains visible forever per spec. */}
                    {Array.isArray(flag.reopen_history) &&
                    flag.reopen_history.length > 0 ? (
                        <details
                            data-testid={`flag-history-${flag.id}`}
                            className="mt-2"
                        >
                            <summary className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 cursor-pointer inline-flex items-center gap-1">
                                <History size={10} />
                                History ({flag.reopen_history.length} cycle
                                {flag.reopen_history.length === 1 ? "" : "s"})
                            </summary>
                            <ul className="mt-2 space-y-2">
                                {flag.reopen_history.map((h, i) => (
                                    <li
                                        key={i}
                                        className="text-xs text-neutral-300 px-2.5 py-1.5 rounded-md bg-secondary/40 border border-white/5"
                                    >
                                        <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                                            Cycle {h.cycle || i + 1}
                                        </div>
                                        {h.resolution_note ? (
                                            <p className="mt-0.5">
                                                <span className="text-emerald-300/80 text-[10px] uppercase tracking-[0.2em] mr-1">
                                                    Resolved
                                                </span>
                                                by{" "}
                                                {h.resolved_by_name || "—"}:{" "}
                                                {h.resolution_note}
                                            </p>
                                        ) : null}
                                        {h.reopen_note ? (
                                            <p className="mt-0.5">
                                                <span className="text-amber-300/90 text-[10px] uppercase tracking-[0.2em] mr-1">
                                                    Reopened
                                                </span>
                                                by{" "}
                                                {h.reopened_by_name || "—"}:{" "}
                                                {h.reopen_note}
                                            </p>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ) : null}
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
                {href ? (
                    <Link
                        to={href}
                        data-testid={`flag-open-${flag.id}`}
                        className="text-[11px] inline-flex items-center gap-1 text-neutral-300 hover:text-white"
                    >
                        Open item
                        <ArrowRight size={11} />
                    </Link>
                ) : null}
                {/* Iter-156A.4 — action order is canonically
                    [I've Seen This] → [Resolve]. The interpretation in
                    real productions is: SEEN = acknowledged, RESOLVED =
                    action completed. We want recipients to think
                    "I've seen this" first (low-friction acknowledgement)
                    before they commit to the higher-stakes "Resolve". To
                    enforce this order we render Ack BEFORE Resolve and
                    group them at the right of the row via ml-auto on the
                    wrapper, not on individual buttons (which previously
                    caused both to fight for the same alignment slot). */}
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    {isActive && iAmRecipient && !iHaveAcked && canAct ? (
                        <Button
                            size="sm"
                            onClick={() => onAcknowledge(flag)}
                            data-testid={`flag-ack-${flag.id}`}
                            className="bg-emerald-500/90 hover:bg-emerald-500 text-neutral-900 font-semibold"
                        >
                            <Check size={13} className="mr-1.5" />
                            I&rsquo;ve seen this
                        </Button>
                    ) : null}
                    {isActive && (iAmRecipient || iAmSender || canManage) && canAct ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onOpenResolve(flag)}
                            data-testid={`flag-resolve-${flag.id}`}
                        >
                            <ShieldCheck size={13} className="mr-1.5" />
                            Resolve
                        </Button>
                    ) : null}
                    {isResolved && (iAmRecipient || iAmSender || canManage) && canAct ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onOpenReopen(flag)}
                            data-testid={`flag-reopen-${flag.id}`}
                        >
                            <RotateCcw size={13} className="mr-1.5" />
                            Reopen
                        </Button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default function FlagsPage() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const { user } = useAuth();

    const [project, setProject] = useState(null);
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("inbox");
    const [resolveTarget, setResolveTarget] = useState(null);
    const [resolveNote, setResolveNote] = useState("");
    const [resolving, setResolving] = useState(false);
    const [reopenTarget, setReopenTarget] = useState(null);
    const [reopenNote, setReopenNote] = useState("");
    const [reopening, setReopening] = useState(false);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    const canManage = isOwner || isAdmin;
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    // Reading flags stays open; ack / resolve / reopen / create
    // affordances hide so we don't tee up actions the backend will
    // 403 (the archive write-lock middleware rejects them).
    const isArchived = Boolean(project?.archived);
    const canActOnFlags = !isArchived;

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data: p } = await api.get(`${apiBase}/${projectId}`);
            setProject(p);
            // Iter-116 PART 3 — load ALL flags I'm involved in (sender
            // OR recipient) regardless of tab so we can split into
            // Inbox / Sent / Archive client-side.
            const elevated = p?.role === "owner" || p?.role === "admin";
            const params = elevated
                ? { status: "all" }
                : { mine: true, status: "all" };
            const { data } = await api.get(`${apiBase}/${projectId}/flags`, {
                params,
            });
            setFlags(data || []);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const filtered = useMemo(() => {
        const isActive = (f) => f.status === "pending" || f.status === "reopened";
        const recipientIds = (f) => (f.recipients || []).map((r) => r.user_id);
        const myUid = user?.id;
        if (tab === "archived") {
            // Iter-116 PART 3 — Archive = resolved flags I'm involved
            // in (sender OR recipient OR admin viewer).
            return flags.filter((f) => f.status === "resolved");
        }
        if (tab === "sent") {
            // Iter-116 PART 3 — Sent = active flags I created.
            // Unresolved sent flags remain visible until resolved.
            return flags.filter(
                (f) => f.flagged_by === myUid && isActive(f)
            );
        }
        // Inbox = active flags directed at me (recipient list or
        // legacy `flagged_for`).
        return flags.filter(
            (f) =>
                isActive(f) &&
                (recipientIds(f).includes(myUid) || f.flagged_for === myUid)
        );
    }, [flags, tab, user]);

    const counts = useMemo(() => {
        const c = { inbox: 0, sent: 0, archived: 0 };
        const myUid = user?.id;
        for (const f of flags) {
            const isActive = f.status === "pending" || f.status === "reopened";
            const rcpts = (f.recipients || []).map((r) => r.user_id);
            const directedAtMe = rcpts.includes(myUid) || f.flagged_for === myUid;
            const sentByMe = f.flagged_by === myUid;
            if (f.status === "resolved") c.archived += 1;
            if (isActive && directedAtMe) c.inbox += 1;
            if (isActive && sentByMe) c.sent += 1;
        }
        return c;
    }, [flags, user]);

    const acknowledge = async (flag) => {
        try {
            await api.post(`${apiBase}/${projectId}/flags/${flag.id}/acknowledge`);
            toast.success("Marked as seen");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const submitResolve = async () => {
        if (!resolveTarget || resolving) return;
        setResolving(true);
        try {
            await api.post(
                `${apiBase}/${projectId}/flags/${resolveTarget.id}/resolve`,
                { note: resolveNote.trim() || null }
            );
            toast.success("Flag resolved");
            setResolveTarget(null);
            setResolveNote("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setResolving(false);
        }
    };

    const submitReopen = async () => {
        if (!reopenTarget || reopening) return;
        const note = reopenNote.trim();
        if (!note) {
            toast.error("Please explain why you're reopening this flag.");
            return;
        }
        setReopening(true);
        try {
            await api.post(
                `${apiBase}/${projectId}/flags/${reopenTarget.id}/reopen`,
                { note }
            );
            toast.success("Flag reopened");
            setReopenTarget(null);
            setReopenNote("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setReopening(false);
        }
    };

    return (
        <div data-testid="flags-page" className="min-h-screen">
            <ProjectHeader
                projectName={project?.name}
                subtitle={canManage ? "Flags · HOD view" : "Flags"}
            />
            {/* Iter-118.9 — body container standardised to the
                shared `max-w-2xl mx-auto px-5` primitive used by
                every other top-level screen, so the back-button
                X position never shifts between Flags and the
                rest of the app. */}
            <main className="max-w-2xl mx-auto px-5 pt-6 pb-32 w-full">
                <header className="mb-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                        {project?.name || ""}
                    </div>
                    <h1 className="font-display text-3xl font-black text-white leading-none mt-1">
                        {canManage ? "Flags" : "Action required"}
                    </h1>
                    <p className="text-xs text-neutral-500 mt-2">
                        {canManage
                            ? "Targeted attention markers across this project. Acknowledge confirms you've seen a flag (personal); resolve closes it for everyone."
                            : "Acknowledge confirms you've seen a flag (personal). Resolve closes it for everyone. Reopen if a resolution is wrong."}
                    </p>
                </header>

                {error ? (
                    <p className="text-sm text-red-400 mb-3">{error}</p>
                ) : null}

                <SegmentedControl
                    value={tab}
                    onChange={setTab}
                    options={TABS.map((t) => ({
                        value: t.id,
                        label: t.label,
                        count: counts[t.id] || 0,
                        testid: `flags-tab-${t.id}`,
                    }))}
                    className="mb-5"
                />

                {loading ? (
                    <ul className="space-y-2" data-testid="flags-loading">
                        {[0, 1, 2].map((i) => (
                            <li
                                key={i}
                                className="h-20 rounded-xl border border-white/10 bg-card animate-pulse"
                            />
                        ))}
                    </ul>
                ) : filtered.length === 0 ? (
                    <div
                        data-testid="flags-empty"
                        className="rounded-xl border border-dashed border-white/10 p-8 text-center"
                    >
                        {tab === "inbox" ? (
                            <>
                                <Inbox
                                    size={22}
                                    className="mx-auto text-neutral-500"
                                />
                                <p className="mt-3 text-sm text-neutral-400">
                                    You&rsquo;re all caught up.
                                </p>
                            </>
                        ) : tab === "sent" ? (
                            <>
                                <Inbox
                                    size={22}
                                    className="mx-auto text-neutral-500"
                                />
                                <p className="mt-3 text-sm text-neutral-400">
                                    No active flags you&rsquo;ve sent.
                                </p>
                            </>
                        ) : (
                            <>
                                <Archive
                                    size={22}
                                    className="mx-auto text-neutral-500"
                                />
                                <p className="mt-3 text-sm text-neutral-400">
                                    Archive is empty.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <ul data-testid="flags-list" className="space-y-2">
                        {filtered.map((f) => (
                            <li key={f.id}>
                                <FlagCard
                                    flag={f}
                                    projectId={projectId}
                                    routeBase={routeBase}
                                    user={user}
                                    canManage={canManage}
                                    canAct={canActOnFlags}
                                    isSentView={tab === "sent"}
                                    onAcknowledge={acknowledge}
                                    onOpenResolve={(flag) => {
                                        setResolveTarget(flag);
                                        setResolveNote("");
                                    }}
                                    onOpenReopen={(flag) => {
                                        setReopenTarget(flag);
                                        setReopenNote("");
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </main>

            <Dialog
                open={!!resolveTarget}
                onOpenChange={(o) => {
                    if (!o) {
                        setResolveTarget(null);
                        setResolveNote("");
                    }
                }}
            >
                <DialogContent
                    data-testid="resolve-dialog"
                    className="max-w-md p-0 overflow-hidden"
                >
                    <DialogHeader className="px-5 pt-5">
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-300" />
                            Resolve flag
                        </DialogTitle>
                    </DialogHeader>
                    <div className="px-5 py-4 space-y-3">
                        <p className="text-xs text-neutral-400">
                            This closes the flag for everyone. Add an optional
                            note explaining how it was handled.
                        </p>
                        <Textarea
                            data-testid="resolve-note-input"
                            value={resolveNote}
                            onChange={(e) => setResolveNote(e.target.value)}
                            placeholder="Resolution note (optional)"
                            rows={3}
                            className="bg-secondary border-white/10 text-sm"
                        />
                    </div>
                    <DialogFooter className="px-5 py-4 bg-card/50 border-t border-white/10 gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setResolveTarget(null)}
                            disabled={resolving}
                            data-testid="resolve-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitResolve}
                            disabled={resolving}
                            data-testid="resolve-submit"
                        >
                            {resolving ? (
                                <Loader2
                                    size={13}
                                    className="mr-1.5 animate-spin"
                                />
                            ) : (
                                <ShieldCheck size={13} className="mr-1.5" />
                            )}
                            Resolve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={!!reopenTarget}
                onOpenChange={(o) => {
                    if (!o) {
                        setReopenTarget(null);
                        setReopenNote("");
                    }
                }}
            >
                <DialogContent
                    data-testid="reopen-dialog"
                    className="max-w-md p-0 overflow-hidden"
                >
                    <DialogHeader className="px-5 pt-5">
                        <DialogTitle className="flex items-center gap-2">
                            <RotateCcw size={16} className="text-amber-300" />
                            Reopen flag
                        </DialogTitle>
                    </DialogHeader>
                    <div className="px-5 py-4 space-y-3">
                        <p className="text-xs text-neutral-400">
                            Reopen if you believe the issue is not actually
                            resolved. A note explaining why is required so the
                            team knows what to look at again. Prior resolution
                            stays visible in history.
                        </p>
                        <Textarea
                            data-testid="reopen-note-input"
                            value={reopenNote}
                            onChange={(e) => setReopenNote(e.target.value)}
                            placeholder="Why are you reopening this? (required)"
                            rows={3}
                            className="bg-secondary border-white/10 text-sm"
                            autoFocus
                        />
                    </div>
                    <DialogFooter className="px-5 py-4 bg-card/50 border-t border-white/10 gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setReopenTarget(null)}
                            disabled={reopening}
                            data-testid="reopen-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitReopen}
                            disabled={reopening || !reopenNote.trim()}
                            data-testid="reopen-submit"
                        >
                            {reopening ? (
                                <Loader2
                                    size={13}
                                    className="mr-1.5 animate-spin"
                                />
                            ) : (
                                <RotateCcw size={13} className="mr-1.5" />
                            )}
                            Reopen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
